import { EditOutlined, HistoryOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import MentenancePlanningService from '../../../../../services/mentenancePlanning/MentenancePlanningService';

import { Search } from '@/DTOS/MentenancePlanning/Search';
import { useHandleResponse } from '@/utils/useHandleResponse';
import { App, Button, Col, DatePicker, Form, Input, Modal, Row, Select, Spin, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAuth } from '../../../../../context/AuthProvider';
import MentenancePlanningHistoryModal from '../../../../../pages/Dispatch/MentenancePlanning/MentanancePlanningAllTab/MentenancePlanningHistoryModal/MentenancePlanningHistoryModal';
import MaintenanceModal from '../../../../../pages/Dispatch/MentenancePlanning/MentenancePlanningModal/MentenancePlanningModal';
import { RootState } from '../../../../../store';
import './Mentenance.css';

const STATUS_COLORS = {
  1: 'default', // Netransmis
  2: 'processing', // Transmis
  3: 'warning', // A ajuns
  4: 'success', // Finalizata
  5: 'error', // Cerere reprogramare
};

const STATUS_LABELS = {
  1: 'Netransmis',
  2: 'Transmis',
  3: 'A ajuns',
  4: 'Finalizata',
  5: 'Cerere reprogramare',
};

const EDITABLE_STATUSES = [1, 2, 5];

// Add this interface for better type safety
interface TableParams {
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
}

const Mentenance: React.FC = () => {
  const reduxContract = useSelector((state: RootState) => state?.models?.contract);
  const tehnicians = useSelector((state: RootState) => state?.store.user.data).filter((user: any) =>
    user?.userPermission?.rols?.some((role: string) => ['tehnician', 'senior_tehnician'].includes(role))
  );
  const uniqueWorkPlaces = Array.from(
    new Map(
      reduxContract.contractWorkPlaceItems
        ?.flatMap((item: any) => item.workPlace) // Extract systemTypes array from each item
        .map((workPlace: any) => [workPlace.id, workPlace]) // Create a Map with id as key
    ).values() // Extract unique objects
  );

  const uniqueSystemTypes = Array.from(
    new Map(
      reduxContract.contractWorkPlaceItems
        ?.flatMap((item: any) => item.systemType) // Extract systemTypes array from each item
        .map((systemType: any) => [systemType.id, systemType]) // Create a Map with id as key
    ).values() // Extract unique objects
  );

  const [editForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });

  const [selectedMaintenances, setSelectedMaintenances] = useState<any>([]);
  const [selectedMonth, setSelectedMonth] = useState<moment.Moment | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<any>(null);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);

  const [isLastMentenanceModalVisible, setLastMentenanceIsModalVisible] = useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const { profile } = useAuth();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();
  const currentDate = moment().format('YYYY-MM-DD');

  const {
    data: mentenanceData,
    isFetching: isMentenancePlanningLoading,
    refetch: refetch,
  } = useQuery({
    queryKey: [
      'mentenancePlannings',
      searchTerm,
      tableParams.pagination.current,
      tableParams.pagination.pageSize,
      selectedMonth,
      selectedStatus,
      selectedPeriod,
    ],
    queryFn: async () => {
      const searchParams: Search = {
        contractId: reduxContract.id,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractIncluded: true,
        isWorkPlaceIncluded: true,
        isAssignedTechnicianIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        termen: searchTerm || null,
        skip: (tableParams.pagination.current - 1) * tableParams.pagination.pageSize,
        take: tableParams.pagination.pageSize,
        month: selectedMonth ? selectedMonth.month() + 1 : null,
        year: selectedMonth ? selectedMonth.year() : null,
        ...(selectedStatus ? { statusIds: [selectedStatus] } : {}),
        ...(selectedPeriod ? { periodicity: selectedPeriod } : {}),
      };

      const response = await MentenancePlanningService.searchAll(searchParams);

      // Update total while preserving current pagination settings
      setTableParams((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          total: response.totalItems || 0,
        },
      }));

      return response;
    },
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const { mutate: saveMentenancePlanning, isPending: isSavingMentenancePlanning } = useMutation({
    mutationFn: (formData: any) => MentenancePlanningService.upsert(formData.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, `Mentenanta ${data?.response?.number} a fost planificata cu succes`);
        // Refetch with current filters and pagination
        refetch();
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const { mutate: getMentenancePlanningByWorkPlace, isPending: isGetingMentenancePlanningByWorkPlace } = useMutation({
    mutationFn: (formData: any) => MentenancePlanningService.searchMentenancePlanningByWorkPlace(formData),
    onSuccess: (data: any) => {
      if (!data) {
        console.error('Received data is not in JSON format or JSON parsing failed.');
        return;
      }
      return data?.response;
    },
    onError: (error: any) => {
      message.error(`Error processing your request: ${error.message}`);
    },
  });

  const handleViewLastMentenance = (record: any) => {
    setSelectedRecord(record); // Store the selected record
    getMentenancePlanningByWorkPlace(
      { workPlaceId: record.workPlace.id, statusIds: [4] }, // Pass the workplace ID
      {
        onSuccess: (data: any) => {
          const records = data?.response || [];

          // Find max number
          const maxNumber = Math.max(...records.map((x: any) => x.interventionFile.number));

          // Find record with max number
          const selected = records.find((x: any) => x.interventionFile.number === maxNumber.toString());

          setSelectedRecord(selected);
          setLastMentenanceIsModalVisible(true); // Show the modal
        },
        onError: (error: any) => {
          message.error(`Failed to fetch maintenances: ${error.message}`);
        },
      }
    );
  };

  const handleViewHistory = (record: any) => {
    getMentenancePlanningByWorkPlace(
      { workPlaceId: record.workPlace.id, statusIds: [4], isInterventionFileIncluded: true },
      {
        onSuccess: (data: any) => {
          setSelectedMaintenances(
            data?.response?.slice().sort((a: any, b: any) => b.interventionFile.number - a.interventionFile.number)
          );
          setIsModalVisible(true); // Show the modal
        },
        onError: (error: any) => {
          message.error(`Failed to fetch maintenances: ${error.message}`);
        },
      }
    );
  };

  const handleMonthChange = useCallback((date: moment.Moment | null) => {
    setSelectedMonth(date);
    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: 1,
      },
    }));
  }, []);

  const handleStatusChange = useCallback((value: number | null) => {
    setSelectedStatus(value);
    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: 1,
      },
    }));
  }, []);

  const handlePeriodChange = useCallback((value: any) => {
    setSelectedPeriod(value);
    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: 1,
      },
    }));
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: 1, // Reset to first page
      },
    }));
  }, []);

  const debouncedSearch = useMemo(() => debounce(handleSearch, 500), []);

  const handleTableChange = (pagination: any['pagination']) => {
    setTableParams((prev) => ({
      ...prev,
      pagination: pagination,
    }));
  };

  const handleEdit = (record?: any) => {
    setSelectedRecord(record || null);
    setIsEditModalVisible(true);

    const hasLaCerere = reduxContract.contractWorkPlaceItems?.some((item: any) => item.period === 'la cerere');

    if (hasLaCerere) {
      setAvailablePeriods(['la cerere']);
    } else {
      setAvailablePeriods(['lunar', 'trimestrial', 'semestrial', 'anual']);
    }

    editForm.setFieldsValue(
      record
        ? {
            company: record?.company || undefined,
            workPlace: record?.workPlace || undefined,
            mentenancePlanningSystemTypes:
              record.mentenancePlanningSystemTypes?.map((type: any) => type?.systemType?.name) || [],
            periodicity: record?.periodicity || undefined,
            dateEnd: record.dateEnd ? new Date(record.dateEnd).toISOString().split('T')[0] : undefined,
            assignedTechnician: record?.assignedTechnician || undefined,
            appointment: record?.appointment ? new Date(record.appointment).toISOString().split('T')[0] : undefined,
            intervalTimetable: record?.intervalTimetable || undefined,
            finalDate: record?.finalDate ? new Date(record.finalDate).toISOString().split('T')[0] : undefined,
            interventionFileId: record?.interventionFileId,
            lastMentenance: record?.lastMentenance,
            history: record?.history,
          }
        : {
            company: reduxContract?.company || undefined,
            workPlace: undefined,
            mentenancePlanningSystemTypes: [],
            periodicity: undefined,
            dateEnd: undefined,
            assignedTechnician: undefined,
            appointment: undefined,
            intervalTimetable: undefined,
            finalDate: reduxContract?.dateEnd
              ? new Date(reduxContract?.dateEnd).toISOString().split('T')[0]
              : undefined,
            interventionFileId: undefined,
            lastMentenance: undefined,
            history: undefined,
          }
    );
  };

  const handleSave = async () => {
    try {
      const values = await editForm.validateFields();
      const statusId =
        tehnicians.find((tehnician: any) => tehnician?.name == values.assignedTechnician?.name) != undefined ? 2 : 1;
      const workPlaceId = uniqueWorkPlaces.find((workPlace: any) => workPlace?.name == values.workPlace?.name)?.id;
      const assignedTechnicianId = tehnicians.find(
        (tehnician: any) => tehnician?.name == values.assignedTechnician?.name
      )?.id;

      const mappedSystemTypes = values.mentenancePlanningSystemTypes.map((selectedTypeName: string) => {
        const systemType = uniqueSystemTypes.find((type: any) => type.name === selectedTypeName);

        // If editing, try to find existing entry to preserve IDs
        const existingEntry = selectedRecord?.mentenancePlanningSystemTypes?.find(
          (existing: any) => existing.systemType.name === selectedTypeName
        );

        return {
          id: existingEntry?.id || '', // Preserve ID if exists
          mentenancePlanningId: existingEntry?.mentenancePlanningId || selectedRecord?.id || '',
          systemType: systemType,
          systemTypeId: systemType?.id,
        };
      });

      const formData = {
        id: selectedRecord?.id || '',
        contractId: reduxContract?.id,
        statusId: statusId,
        companyId: reduxContract?.companyId,
        workPlaceId: workPlaceId,
        assignedTechnicianId: assignedTechnicianId,
        mentenancePlanningSystemTypes: mappedSystemTypes,
        interventionFileId: values?.interventionFileId,
        lastMentenance: values?.lastMentenance,
        intervalTimetable: values?.intervalTimetable,
        registeredByUserId: profile?.id,
        history: values?.history ?? [`[Inregistrata] - ${dayjs().format('DD-MM-YYYY HH:mm')}]`],
        appointment: values?.appointment,
        dateEnd: values?.dateEnd,
        periodicity: values?.periodicity,
        finalDate: values?.finalDate,
      };

      saveMentenancePlanning(formData);
      setIsEditModalVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const getRowClassName = (record: any) => {
    if (record.status?.id === 4) return 'finalized-row';

    if ([1, 2].includes(record.status?.id) && record.appointment) {
      const daysUntilAppointment = moment(record.appointment).diff(moment(), 'days');
      if (daysUntilAppointment <= 5) return 'urgent-row';
      if (daysUntilAppointment <= 10) return 'warning-row';
    }
    return '';
  };

  const onDateChange = (dateField: string, dateValue: string) => {
    editForm.setFieldsValue({ [dateField]: dateValue });
  };

  const baseColumns = [
    { title: 'Numar', dataIndex: 'number', render: (number: any) => <Tag color={'blue'}>{number}</Tag> },
    {
      title: 'Beneficiar',
      key: 'company',
      render: (_: any, record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
    },
    { title: 'Punct', dataIndex: ['workPlace', 'name'] },
    {
      title: 'Sistem',
      dataIndex: 'mentenancePlanningSystemTypes',
      key: 'mentenancePlanningSystemTypes',
      render: (mentenancePlanningSystemTypes: any[]) => (
        <>
          {mentenancePlanningSystemTypes && mentenancePlanningSystemTypes.length > 0 && (
            <span>
              {mentenancePlanningSystemTypes.length > 0
                ? mentenancePlanningSystemTypes?.map((type: any) => `[${type.systemType.name}]`).join(' ')
                : ''}
            </span>
          )}
        </>
      ),
    },
    { title: 'Periodicitate', dataIndex: 'periodicity' },
    {
      title: 'Numar Contract',
      dataIndex: 'contract',
      render: (contract: any) => {
        return <>{contract?.number}</>;
      },
    },
    {
      title: 'Termen limita',
      dataIndex: 'dateEnd',
      render: (_value: any, record: any, _index: any) => {
        return (
          <>
            <span>{record.dateEnd ? moment(record.dateEnd).format('DD-MM-YYYY') : '-'}</span>
          </>
        );
      },
    },
    {
      title: 'Data Expirare Contract',
      dataIndex: 'finalDate',
      render: (_value: any, record: any, _index: any) => {
        return (
          <>
            <span>{record.finalDate ? moment(record.finalDate).format('DD-MM-YYYY') : '-'}</span>
          </>
        );
      },
    },
    {
      title: 'Ultima Mentenanta',
      dataIndex: 'lastMentenance',
      render: (_value: any, record: any) => {
        return (
          <>
            {!!record.lastMentenance ? (
              <Button onClick={() => handleViewLastMentenance(record)}>
                <span>{moment(record.lastMentenance).format('DD-MM-YYYY')}</span>
              </Button>
            ) : (
              <span>-</span>
            )}
          </>
        );
      },
    },
    {
      title: 'Programare',
      dataIndex: '',
      render: (_value: any, record: any) => {
        if (!record.appointment) return <span>-</span>;

        const appointmentDate = moment(record.appointment);
        const daysUntilAppointment = appointmentDate.diff(moment(), 'days');

        let tagClassName = 'appointment-tag';
        if ([1, 2].includes(record.status?.id)) {
          if (daysUntilAppointment <= 5) tagClassName += ' appointment-tag-urgent';
          else if (daysUntilAppointment <= 10) tagClassName += ' appointment-tag-warning';
        }

        return (
          <Tag className={tagClassName}>
            {appointmentDate.format('DD-MM-YYYY')}
            {record.intervalTimetable && ` ${record.intervalTimetable}`}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: ['status', 'name'],
      render: (_name: string, record: any) => (
        <Tag color={STATUS_COLORS[record.status?.id as keyof typeof STATUS_COLORS]} className='status-tag'>
          {STATUS_LABELS[record.status?.id as keyof typeof STATUS_LABELS]}
        </Tag>
      ),
    },
    {
      title: 'Istoric',
      dataIndex: '',
      render: (record: any) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button icon={<HistoryOutlined />} onClick={() => handleViewHistory(record)}></Button>
          </div>
        );
      },
    },
    {
      title: 'Actiuni',
      dataIndex: '',
      render: (record: any) => {
        const isEditable =
          EDITABLE_STATUSES.includes(record.status?.id) && profile?.userPermission?.rols?.includes('dispatcher');
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={!isEditable}
              title={!isEditable ? 'Mentenanta nu poate fi editată în statusul curent' : ''}
              className={isEditable ? 'action-button' : 'action-button-disabled'}
            />
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (mentenanceData?.total) {
      setTableParams({
        pagination: {
          ...tableParams.pagination,
          total: mentenanceData.total,
        },
      });
    }
  }, [mentenanceData?.total]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const isLoading = isMentenancePlanningLoading || isGetingMentenancePlanningByWorkPlace || isSavingMentenancePlanning;

  if (isLoading) {
    return <Spin className='spinner' size='large'></Spin>;
  }

  return (
    <div className='mentenance-container'>
      <div className='mentenance-title-and-create-button'>
        <h1 className='mentenance-title'>Mentenante </h1>
        <Button type='primary' onClick={() => handleEdit(undefined)} className='create-button' icon={<PlusOutlined />}>
          Creaza o noua mentenanta
        </Button>
      </div>

      <div className='filters-container'>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6} className='filter-col'>
            <Input.Search
              placeholder='Cauta...'
              defaultValue={searchTerm}
              onChange={(e) => debouncedSearch(e.target.value)}
              allowClear={true}
              autoFocus
              size='large'
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} className='filter-col'>
            <DatePicker
              value={selectedMonth}
              picker='month'
              placeholder='Filtreaza dupa luna'
              onChange={handleMonthChange}
              allowClear
              style={{ width: '100%' }}
              size='large'
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} className='filter-col'>
            <Select
              placeholder='Filtreaza dupa status'
              allowClear
              value={selectedStatus}
              onChange={handleStatusChange}
              style={{ width: '100%' }}
              size='large'
            >
              {Object.entries(STATUS_LABELS).map(([key, label]: any) => (
                <Select.Option key={key} value={Number(key)}>
                  <span>{label}</span>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} className='filter-col'>
            <Select
              placeholder='Filtreaza dupa perioada'
              allowClear
              value={selectedPeriod}
              onChange={handlePeriodChange}
              style={{ width: '100%' }}
              size='large'
            >
              {['lunar', 'trimestrial', 'semestrial', 'anual', 'la cerere'].map((period: any, index: any) => (
                <Select.Option key={index} value={period}>
                  <span>{period}</span>
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
      </div>

      <Table
        className='mentenance-table'
        columns={baseColumns}
        dataSource={mentenanceData?.response?.map((item: any) => ({ ...item, key: item.number }))}
        pagination={{
          ...tableParams.pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} înregistrări`,
          pageSizeOptions: ['10', '20', '50'],
        }}
        rowClassName={getRowClassName}
        onChange={handleTableChange}
        loading={isLoading}
        size='middle'
        scroll={{ x: 'max-content' }}
      />

      <MaintenanceModal
        isModalVisible={isLastMentenanceModalVisible}
        handleModalClose={() => setLastMentenanceIsModalVisible(false)}
        selectedRecord={selectedRecord} // The most recent maintenance for the workplace
      ></MaintenanceModal>

      <MentenancePlanningHistoryModal
        visible={isModalVisible}
        maintenances={selectedMaintenances} // All maintenances for the workplace
        onClose={() => setIsModalVisible(false)}
      />

      <Modal
        title={`${selectedRecord ? 'Editare' : 'Creeaza'} Mentenanta`}
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={handleSave}
        okText='Salvează'
        cancelText='Anulează'
      >
        <Form form={editForm} layout='vertical'>
          <Form.Item
            name={['company', 'name']}
            label='Beneficiar'
            rules={[{ required: true, message: 'Selectati un beneficiar' }]}
          >
            <Select disabled placeholder='Selectati beneficiarul'></Select>
          </Form.Item>

          <Form.Item
            name={['workPlace', 'name']}
            label='Punct de lucru'
            rules={[{ required: true, message: 'Selectati un punct de lucru' }]}
          >
            <Select placeholder='Selectati punctul de lucru'>
              {uniqueWorkPlaces
                ?.filter((location: any) => location?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()))
                .map((location: any, index: any) => (
                  <Select.Option key={index} value={location?.name}>
                    {location?.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name='mentenancePlanningSystemTypes'
            label='Sistem'
            rules={[{ required: true, message: 'Selectati sistemele' }]}
          >
            <Select mode='multiple' placeholder='Selectati sistemul'>
              {uniqueSystemTypes?.map((systemType: any, index: any) => (
                <Select.Option key={index} value={systemType?.name}>
                  {systemType?.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name='periodicity'
            label='Periodicitate'
            rules={[{ required: true, message: 'Selectati un periodicitatea' }]}
          >
            <Select placeholder='Selectati periodicitatea'>
              {availablePeriods.map((item: any, index: any) => (
                <Select.Option key={index} value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name='dateEnd'
            label='Termen limita'
            rules={[{ required: true, message: 'Selectati un termenul limita' }]}
          >
            <Input type='date' min={currentDate} onChange={(e) => onDateChange('dateEnd', e.target.value)} />
          </Form.Item>

          <Form.Item
            name={['assignedTechnician', 'name']}
            label='Tehnician'
            dependencies={['appointment']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value && getFieldValue('appointment')) {
                    return Promise.reject(
                      new Error('Dacă selectați o zi de programare, trebuie să selectați și un tehnician.')
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Select placeholder='Selectati tehnicianul'>
              {tehnicians?.map((tehnician: any, index: any) => (
                <Select.Option key={index} value={tehnician?.name}>
                  {tehnician?.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='appointment'
                label='Ziua programarii'
                dependencies={['assignedTechnician']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value && getFieldValue(['assignedTechnician', 'name'])) {
                        return Promise.reject(
                          new Error('Dacă selectați un tehnician, trebuie să setați și o zi de programare.')
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type='date' min={currentDate} onChange={(e) => onDateChange('appointment', e.target.value)} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='intervalTimetable' label='Interval orar'>
                <Input placeholder='Ex: 09:00-12:00' />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name='finalDate' label='Data expirare contract'>
            <Input type='date' disabled min={currentDate} onChange={(e) => onDateChange('finalDate', e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Mentenance;
